import React, { useState } from 'react';
import { useParams } from 'react-router';
import { useHistory, Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import Button from '@atlaskit/button';
import EmptyState from '@atlaskit/empty-state';
import styled from '@emotion/styled';
import { useFeatureToggle, useApplicationContext, AppContent } from '@css-xp/base';
import { initialCustomer } from './order-page';
import { useOpenClose } from '@css-xp/ppc-custom-hooks';
import { getRouteUrl } from '@css-xp/ppc-utils';
import { PageBackgroundV2 } from '@css-xp/page-background';
import { transformBillingProfileDetails } from './utils';
import { CreateCustomerAndBillingProfileModalV2 } from './ui/customer/src/components/create-customer-and-billing-profile-modal-v2';
import { STATSIG_FEATURE_GATES, routePaths } from '@css-xp/ppc-constants';
import { fetchTransactionAccountForCreateBillingProfile } from '@css-xp/ppc-billing-profile-table';
import {
  FetchTransactionAccountForCreateBillingProfileQuery,
  FetchTransactionAccountForCreateBillingProfileQueryVariables,
} from '@css-xp/graphtypes/src/generated-types-new';
import { useStore } from './store';
import { SearchCustomerContainer } from './ui/customer';
import { CartCustomerSearchItem } from './ui/customer/src/types';
// @ts-ignore
import EmptySelectedCustomerIcon from './ui/assets/empty-customer-selection.svg';
import { BillingProfileResponse } from './types';
import { CART_ROUTE_PARAMS } from './constants';

const StyledEmptySelectedCustomer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 60vh;
`;

const StyledCancelOrderCTA = styled.div`
  a {
    button {
      :hover {
        text-decoration: none;
      }
    }
  }
`;

export const SelectCustomerPage = () => {
  const { txaId } = useParams<{ txaId: string }>();
  const [isEnabled] = useFeatureToggle();
  const isDesignIteration1Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_DESIGN_ITERATION_1);
  const history = useHistory();
  const context = useApplicationContext();
  const isLoggedInUserCustomerAdvocate = context?.isCustomerAdvocate;
  const isSoldToAddressArchivalEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_ARCHIVE_SOLD_TO_ADDRESS);
  const [, actions] = useStore();
  const [customer, setCustomer] = useState<CartCustomerSearchItem>(initialCustomer);
  const [hasAddedNewCustomer, setHasAddedNewCustomer] = useState(false);

  const {
    isOpen,
    open: openCreateCustomerAndBillingProfileModal,
    close: closeCreateCustomerAndBillingProfileModal,
  } = useOpenClose();

  /** if this URL is opened from old design by direct txa switch, redirect to home screen */
  if (!isDesignIteration1Enabled) {
    history.push(
      getRouteUrl(routePaths.home, {
        txaId: txaId,
      })
    );
  }

  const { data: transactionAccountData, loading: transactionAccountDataLoading } = useQuery<
    FetchTransactionAccountForCreateBillingProfileQuery,
    FetchTransactionAccountForCreateBillingProfileQueryVariables
  >(fetchTransactionAccountForCreateBillingProfile, { variables: { id: txaId } });

  const navigateToSelectBillingProfileScreen = (customer: CartCustomerSearchItem) => {
    const params = new URLSearchParams({ [CART_ROUTE_PARAMS.CUSTOMER]: customer.name });
    history.push({
      pathname: getRouteUrl(routePaths.cart, {
        txaId: txaId as string,
      }),
      search: params.toString(),
    });
  };

  const navigateToCartScreen = (
    invoiceGroupId: string,
    customerName: string,
    hasCreatedNewCustomerAndBillingProfile?: boolean
  ) => {
    const params = new URLSearchParams({
      [CART_ROUTE_PARAMS.BILLING_PROFILE]: invoiceGroupId,
      [CART_ROUTE_PARAMS.CUSTOMER]: customerName,
    });
    history.push(
      {
        pathname: getRouteUrl(routePaths.cart, {
          txaId: txaId as string,
        }),
        search: params.toString(),
      },
      {
        hasCreatedNewCustomerAndBillingProfile: hasCreatedNewCustomerAndBillingProfile,
      }
    );
  };

  const onBillingProfileSubmit = (responsePayload?: {
    customerName?: string | null;
    customerId?: string;
    billingProfileInfo?: BillingProfileResponse;
  }) => {
    setHasAddedNewCustomer(true);
    if (responsePayload?.customerName && responsePayload?.customerId) {
      setCustomer({ name: responsePayload?.customerName, ids: [responsePayload?.customerId] });
      actions.setSelectedCustomer({ name: responsePayload?.customerName, ids: [responsePayload?.customerId] });
    }
    if (responsePayload?.billingProfileInfo?.id && responsePayload?.billingProfileInfo?.name) {
      actions.setInvoiceGroup(responsePayload?.billingProfileInfo?.id);
      actions.setBillingProfiles([transformBillingProfileDetails(responsePayload?.billingProfileInfo)]);
    }
    closeCreateCustomerAndBillingProfileModal();
    navigateToCartScreen(
      responsePayload?.billingProfileInfo?.id as string,
      responsePayload?.customerName as string,
      true
    );
  };

  const handleCustomerChange = (customer: CartCustomerSearchItem) => {
    actions.setSelectedCustomer(customer);
    actions.resetStore();
    navigateToSelectBillingProfileScreen(customer);
  };

  const renderSelectCustomerComponent = () => {
    return (
      <SearchCustomerContainer
        onSelectCustomer={handleCustomerChange}
        isCreateCustomerEnabled={true}
        isFetchCustomerViaCCPEnabled={!isLoggedInUserCustomerAdvocate}
        onAddCustomerClick={() => {
          openCreateCustomerAndBillingProfileModal();
        }}
        selectedCustomer={hasAddedNewCustomer ? customer : null}
        isSoldToAddressArchivalEnabled={isSoldToAddressArchivalEnabled}
      />
    );
  };

  const renderCreateCustomerAndBillingProfileModal = () => {
    return (
      <CreateCustomerAndBillingProfileModalV2
        isOpen={isOpen}
        transactionAccountId={txaId}
        onClose={closeCreateCustomerAndBillingProfileModal}
        onSubmit={onBillingProfileSubmit}
        transactionAccountData={transactionAccountData}
        transactionAccountDataLoading={transactionAccountDataLoading}
        isBPForMonthlyOrders={true}
        isSoldToAddressArchivalEnabled={isSoldToAddressArchivalEnabled}
      />
    );
  };

  const EmptySelectedCustomer = () => {
    return (
      <StyledEmptySelectedCustomer>
        <EmptyState
          header="You’re about to create a new order"
          description="Please select an existing customer or create a new one to choose the appropriate billing profile."
          imageUrl={EmptySelectedCustomerIcon as any}
        />
      </StyledEmptySelectedCustomer>
    );
  };

  const renderCancelOrderCTA = () => {
    return (
      <StyledCancelOrderCTA>
        <Link
          to={getRouteUrl(routePaths.home, {
            txaId: txaId,
          })}
        >
          <Button>Cancel order</Button>
        </Link>
      </StyledCancelOrderCTA>
    );
  };

  const renderAppContent = () => {
    return (
      <AppContent>
        {renderSelectCustomerComponent()}
        {<EmptySelectedCustomer />}
        {isOpen && renderCreateCustomerAndBillingProfileModal()}
      </AppContent>
    );
  };

  const breadcrumbsItems = [
    {
      title: 'Home',
      href: routePaths.home,
    },
    {
      title: 'Select customer',
      href: routePaths.cart,
    },
  ];

  return (
    <PageBackgroundV2
      heading={'Select customer'}
      breadcrumbsProps={{ items: breadcrumbsItems }}
      actions={renderCancelOrderCTA()}
    >
      {renderAppContent()}
    </PageBackgroundV2>
  );
};
